
import { supabase } from "@/integrations/supabase/client";
import { EnhancedGiftTracking } from "@/types/custom-types";

// Fetch all gift tracking records
export const fetchGiftTrackingRecords = async (): Promise<EnhancedGiftTracking[]> => {
  const { data, error } = await supabase
    .from('enhanced_gift_tracking')
    .select(`
      *,
      contact:contact_id (id, first_name, last_name, email),
      organization:organization_id (id, name),
      gift:gift_id (id, gift_name, category, cost, image_url),
      sent_by_user:sent_by (id, first_name, last_name),
      approved_by_user:approved_by (id, first_name, last_name)
    `)
    .order('date_sent', { ascending: false });
    
  if (error) throw error;
  
  // Process the data to handle potential error objects in relations
  const processedData = data.map(gift => {
    return {
      ...gift,
      contact: processRelation(gift.contact),
      organization: processRelation(gift.organization),
      gift: processRelation(gift.gift),
      sent_by_user: processRelation(gift.sent_by_user),
      approved_by_user: processRelation(gift.approved_by_user)
    };
  });
  
  return processedData as EnhancedGiftTracking[];
};

// Helper function to process relation objects
function processRelation(relation: any) {
  if (!relation) return undefined;
  
  if (typeof relation === 'object' && 'error' in relation) {
    return undefined;
  }
  
  return relation;
}

// Fetch gift tracking records for a specific contact
export const fetchContactGiftRecords = async (contactId: string): Promise<EnhancedGiftTracking[]> => {
  const { data, error } = await supabase
    .from('enhanced_gift_tracking')
    .select(`
      *,
      contact:contact_id (id, first_name, last_name, email),
      organization:organization_id (id, name),
      gift:gift_id (id, gift_name, category, cost, image_url),
      sent_by_user:sent_by (id, first_name, last_name),
      approved_by_user:approved_by (id, first_name, last_name)
    `)
    .eq('contact_id', contactId)
    .order('date_sent', { ascending: false });
    
  if (error) throw error;
  
  // Process the data to handle potential error objects
  const processedData = data.map(gift => {
    return {
      ...gift,
      contact: processRelation(gift.contact),
      organization: processRelation(gift.organization),
      gift: processRelation(gift.gift),
      sent_by_user: processRelation(gift.sent_by_user),
      approved_by_user: processRelation(gift.approved_by_user)
    };
  });
  
  return processedData as EnhancedGiftTracking[];
};

// Fetch gift tracking records for a specific organization
export const fetchOrganizationGiftRecords = async (organizationId: string): Promise<EnhancedGiftTracking[]> => {
  const { data, error } = await supabase
    .from('enhanced_gift_tracking')
    .select(`
      *,
      contact:contact_id (id, first_name, last_name, email),
      organization:organization_id (id, name),
      gift:gift_id (id, gift_name, category, cost, image_url),
      sent_by_user:sent_by (id, first_name, last_name),
      approved_by_user:approved_by (id, first_name, last_name)
    `)
    .eq('organization_id', organizationId)
    .order('date_sent', { ascending: false });
    
  if (error) throw error;
  
  // Process the data to handle potential error objects
  const processedData = data.map(gift => {
    return {
      ...gift,
      contact: processRelation(gift.contact),
      organization: processRelation(gift.organization),
      gift: processRelation(gift.gift),
      sent_by_user: processRelation(gift.sent_by_user),
      approved_by_user: processRelation(gift.approved_by_user)
    };
  });
  
  return processedData as EnhancedGiftTracking[];
};

// Fetch a specific gift record
export const fetchGiftRecordById = async (id: string): Promise<EnhancedGiftTracking> => {
  const { data, error } = await supabase
    .from('enhanced_gift_tracking')
    .select(`
      *,
      contact:contact_id (id, first_name, last_name, email),
      organization:organization_id (id, name),
      gift:gift_id (id, gift_name, category, cost, image_url),
      sent_by_user:sent_by (id, first_name, last_name),
      approved_by_user:approved_by (id, first_name, last_name)
    `)
    .eq('id', id)
    .single();
    
  if (error) throw error;
  
  // Process the data to handle potential error objects
  const processedData = {
    ...data,
    contact: processRelation(data.contact),
    organization: processRelation(data.organization),
    gift: processRelation(data.gift),
    sent_by_user: processRelation(data.sent_by_user),
    approved_by_user: processRelation(data.approved_by_user)
  };
  
  return processedData as EnhancedGiftTracking;
};

// Create a gift tracking record
export const createGiftRecord = async (giftData: Partial<EnhancedGiftTracking>): Promise<EnhancedGiftTracking> => {
  const { data, error } = await supabase
    .from('enhanced_gift_tracking')
    .insert(giftData)
    .select()
    .single();
    
  if (error) throw error;
  return data as EnhancedGiftTracking;
};

// Update a gift tracking record
export const updateGiftRecord = async (id: string, giftData: Partial<EnhancedGiftTracking>): Promise<EnhancedGiftTracking> => {
  const { data, error } = await supabase
    .from('enhanced_gift_tracking')
    .update(giftData)
    .eq('id', id)
    .select()
    .single();
    
  if (error) throw error;
  return data as EnhancedGiftTracking;
};

// Delete a gift tracking record
export const deleteGiftRecord = async (id: string): Promise<boolean> => {
  const { error } = await supabase
    .from('enhanced_gift_tracking')
    .delete()
    .eq('id', id);
    
  if (error) throw error;
  return true;
};

// Get gift tracking analytics
export const getGiftAnalytics = async (year?: number) => {
  const currentYear = year || new Date().getFullYear();
  
  // Get gifts sent this year
  const { data: yearGifts, error: yearError } = await supabase
    .from('enhanced_gift_tracking')
    .select('date_sent, gift:gift_id (gift_name, cost)')
    .gte('date_sent', `${currentYear}-01-01`)
    .lte('date_sent', `${currentYear}-12-31`);
    
  if (yearError) throw yearError;
  
  // Get total gift count by month
  const giftsByMonth = Array(12).fill(0);
  yearGifts?.forEach(gift => {
    if (gift.date_sent) {
      const month = new Date(gift.date_sent).getMonth();
      giftsByMonth[month]++;
    }
  });
  
  // Calculate total spend
  const totalSpend = yearGifts?.reduce((sum, gift) => {
    return sum + (gift.gift?.cost || 0);
  }, 0) || 0;
  
  // Get top recipients by counting contact_id occurrences
  const { data: contacts, error: contactsError } = await supabase
    .from('enhanced_gift_tracking')
    .select(`
      contact_id,
      count: contact_id,
      contact:contact_id (first_name, last_name)
    `)
    .gte('date_sent', `${currentYear}-01-01`)
    .not('contact_id', 'is', null);
    
  if (contactsError) throw contactsError;
  
  // Process the contacts to create a frequency map
  const contactFrequency = {};
  contacts?.forEach(record => {
    const contactId = record.contact_id;
    if (contactId) {
      if (!contactFrequency[contactId]) {
        contactFrequency[contactId] = { 
          count: 0,
          contact: record.contact
        };
      }
      contactFrequency[contactId].count++;
    }
  });
  
  // Convert to array and sort by count
  const topRecipients = Object.entries(contactFrequency)
    .map(([id, data]: [string, any]) => ({
      contact_id: id,
      count: data.count,
      contact: data.contact
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);
  
  return {
    totalCount: yearGifts?.length || 0,
    totalSpend,
    giftsByMonth,
    topRecipients,
    year: currentYear
  };
};
